import { useState, type FormEvent } from 'react';
import { useQuery } from '@tanstack/react-query';
import { passwordProblem } from '@shared/constants';
import { ApiError, api, errorMessage } from '@/lib/api';
import { formatDayTime } from '@/lib/format';
import { Avatar } from '@/ui/Avatar';
import { Callout, Loading } from '@/ui/bits';
import { InputField } from '@/ui/Field';
import { GoogleMark, Icon } from '@/ui/Icon';
import { AuthBrand, AuthFrame } from './AuthFrame';

// The page behind a one-time link from the super admin: joining the team, or choosing a new password.

interface InviteInfo {
  kind: 'invite' | 'reset';
  name: string;
  hue: number;
  email: string | null;
  invitedBy: string | null;
  workspaceName: string;
  google: boolean;
  expiresAt: string;
}

/** The token when the address is an access link (/invite/<token>), otherwise null. */
export function accessLinkToken(): string | null {
  const m = /^\/invite\/([A-Za-z0-9_-]+)\/?$/.exec(window.location.pathname);
  return m ? m[1]! : null;
}

export function InviteAccept({ token, notice, onDone }: { token: string; notice: string | null; onDone: () => void }) {
  const { data, error, isPending } = useQuery({
    queryKey: ['invite', token],
    queryFn: () => api.get<InviteInfo>(`/invites/${encodeURIComponent(token)}`),
    retry: false,
    staleTime: Infinity,
  });

  const leave = () => {
    window.history.replaceState(null, '', '/');
    onDone();
  };

  if (isPending) {
    return (
      <AuthFrame label="Opening your link">
        <div className="auth-card">
          <AuthBrand />
          <Loading label="Opening your link" />
        </div>
      </AuthFrame>
    );
  }

  if (error || !data) {
    const gone = error instanceof ApiError && (error.status === 404 || error.status === 410);
    return (
      <AuthFrame label="This link cannot be used">
        <div className="auth-card">
          <AuthBrand />
          <h1 className="auth-h">{gone ? 'This link cannot be used' : 'This link did not open'}</h1>
          <div className="auth-body">
            <Callout tone={gone ? 'warn' : 'danger'} role="alert">
              {error instanceof ApiError && error.status === 404
                ? 'This link is not valid. Ask your super admin for a new one.'
                : errorMessage(error)}
            </Callout>
            <button type="button" className="btn btn-primary btn-lg" onClick={leave}>
              Go to sign in
            </button>
          </div>
        </div>
      </AuthFrame>
    );
  }

  return <AcceptForm token={token} info={data} notice={notice} onDone={leave} />;
}

function AcceptForm({ token, info, notice, onDone }: { token: string; info: InviteInfo; notice: string | null; onDone: () => void }) {
  const [password, setPassword] = useState('');
  const [again, setAgain] = useState('');
  const [fields, setFields] = useState<Record<string, string>>({});
  const [problem, setProblem] = useState<string | null>(notice);
  const [busy, setBusy] = useState(false);
  const reset = info.kind === 'reset';

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (busy) return;
    const local: Record<string, string> = {};
    const rule = passwordProblem(password);
    if (rule) local.password = rule;
    else if (again !== password) local.again = 'The two passwords are different.';
    setFields(local);
    if (Object.keys(local).length) return;
    setBusy(true);
    setProblem(null);
    try {
      await api.post(`/invites/${encodeURIComponent(token)}/accept`, { password });
      onDone();
    } catch (err) {
      if (err instanceof ApiError && Object.keys(err.fields).length) setFields(err.fields);
      else setProblem(errorMessage(err));
      setBusy(false);
    }
  };

  return (
    <AuthFrame label={reset ? 'Choose a new password' : `Join ${info.workspaceName}`}>
      <div className="auth-card">
        <AuthBrand workspaceName={info.workspaceName} />
        <h1 className="auth-h">{reset ? 'Choose a new password' : `Join ${info.workspaceName}`}</h1>
        <p className="auth-sub">
          {reset
            ? `${info.invitedBy ? `${info.invitedBy} sent you this link to set a new password.` : 'This link sets a new password for your account.'} Saving it signs you out on your other devices.`
            : `${info.invitedBy ?? 'Your super admin'} invited you to the team's client book. ${info.google ? 'Choose how you want to sign in.' : 'Choose a password to sign in with.'}`}
        </p>
        <div className="auth-invite">
          <Avatar name={info.name} hue={info.hue} size="md" />
          <div className="grow">
            <b>{info.name}</b>
            <span>{info.email ?? 'No email on file'}</span>
          </div>
        </div>
        <div className="auth-body">
          {problem ? (
            <Callout tone="danger" role="alert">
              {problem}
            </Callout>
          ) : null}
          {!reset && info.google ? (
            <>
              <a className="btn btn-lg auth-google" href={`/auth/google/start?purpose=invite&invite=${encodeURIComponent(token)}`}>
                <GoogleMark />
                Join with Google
              </a>
              <div className="auth-or">or choose a password</div>
            </>
          ) : null}
          <form className="auth-form" onSubmit={submit} noValidate>
            {info.email ? <input type="email" name="username" autoComplete="username" value={info.email} readOnly hidden /> : null}
            <InputField
              label={reset ? 'New password' : 'Password'}
              type="password"
              name="new-password"
              autoComplete="new-password"
              autoFocus={reset || !info.google}
              help="At least 10 characters. A short sentence works well."
              value={password}
              error={fields.password}
              onChange={(e) => setPassword(e.target.value)}
            />
            <InputField
              label="Type it again"
              type="password"
              name="new-password-again"
              autoComplete="new-password"
              value={again}
              error={fields.again}
              onChange={(e) => setAgain(e.target.value)}
            />
            <button type="submit" className="btn btn-primary btn-lg" disabled={busy} aria-busy={busy || undefined}>
              {busy ? 'Saving' : reset ? 'Save and sign in' : `Join ${info.workspaceName}`}
            </button>
          </form>
        </div>
      </div>
      <p className="auth-foot">
        <Icon name="clock" />
        This link works until {formatDayTime(info.expiresAt)}.
      </p>
    </AuthFrame>
  );
}
